"use client";

import { c as _c } from "react/compiler-runtime";
import { $convertFromMarkdownString } from "@lexical/markdown";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $createParagraphNode, $createTextNode, $getRoot, $isElementNode, COMMAND_PRIORITY_HIGH, KEY_ENTER_COMMAND, KEY_ESCAPE_COMMAND, type EditorState, type LexicalNode, type RootNode, type TextNode } from "lexical";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { $isCodeNode, CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import ExampleTheme from "@/components/drops/create/lexical/ExampleTheme";
import { EmojiNode } from "@/components/drops/create/lexical/nodes/EmojiNode";
import { HashtagNode } from "@/components/drops/create/lexical/nodes/HashtagNode";
import { $createWaveMentionNode, WaveMentionNode } from "@/components/drops/create/lexical/nodes/WaveMentionNode";
import { $createMentionNode, MentionNode } from "@/components/drops/create/lexical/nodes/MentionNode";
import EmojiPlugin from "@/components/drops/create/lexical/plugins/emoji/EmojiPlugin";
import type { NewMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewWaveMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "@/components/drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
import { HASHTAG_TRANSFORMER } from "@/components/drops/create/lexical/transformers/HastagTransformer";
import { SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import { MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/MentionTransformer";
import { WAVE_MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/WaveMentionTransformer";
import type { MentionedUser, MentionedWave } from "@/entities/IDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import CreateDropEmojiPicker from "../CreateDropEmojiPicker";
import { addBlankLinePlaceholders, removeBlankLinePlaceholders } from "./blankLinePlaceholders";
import { exportDropMarkdown, normalizeDropMarkdown } from "./normalizeDropMarkdown";
interface EditDropLexicalProps {
  readonly initialContent: string;
  readonly initialMentions: ApiDropMentionedUser[];
  readonly initialWaveMentions: ApiMentionedWave[];
  readonly waveId: string | null;
  readonly isSaving: boolean;
  readonly onSave: (content: string, mentions: ApiDropMentionedUser[], mentionedWaves: ApiMentionedWave[]) => void;
  readonly onCancel: () => void;
}
const MAX_MENTION_RECONSTRUCTION_PASSES = 20;
const EDIT_MARKDOWN_TRANSFORMERS = [...SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE, MENTION_TRANSFORMER, HASHTAG_TRANSFORMER, WAVE_MENTION_TRANSFORMER];
const convertCodeNodesToFences = (root: RootNode) => {
  const stack: LexicalNode[] = [...root.getChildren()];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if ($isCodeNode(node)) {
      const language = node.getLanguage() ?? "";
      const safeLanguage = language.trim().replaceAll(/[`\n\r]/g, "");
      const codeText = node.getTextContent();
      const normalizedCode = codeText.endsWith("\n") ? codeText : `${codeText}\n`;
      const maxExistingFence = (codeText.match(/`+/g) ?? []).reduce((max, match) => Math.max(max, match.length), 0);
      const fenceLength = Math.max(3, maxExistingFence + 1);
      const fence = "`".repeat(fenceLength);
      const openFence = safeLanguage ? `${fence}${safeLanguage}` : fence;
      const fencedMarkdown = `${openFence}\n${normalizedCode}${fence}`;
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(fencedMarkdown));
      node.replace(paragraph);
      continue;
    }
    if ($isElementNode(node)) {
      stack.push(...node.getChildren());
    }
  }
};
function reconstructSplitMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /@\[(\w+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const handle = mentionMatch[1];
  if (!handle) return false;
  const mentionNode = $createMentionNode(`@${handle}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function reconstructSplitWaveMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /#\[([^\]]+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const waveName = mentionMatch[1];
  const mentionNode = $createWaveMentionNode(`#${waveName}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function processSplitMentions(textNodes: Array<TextNode>): boolean {
  for (let i = 0; i < textNodes.length - 1; i++) {
    const currentNode = textNodes[i];
    const nextNode = textNodes[i + 1];
    const currentText = currentNode?.getTextContent();
    const nextText = nextNode?.getTextContent();
    const mentionStart = currentText?.match(/@\[\w*$/);
    const mentionEnd = nextText?.match(/^\w*\]/);
    if (mentionStart && mentionEnd) {
      try {
        if (reconstructSplitMention(currentNode, nextNode, mentionStart, mentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split mention", error);
      }
    }
    const waveMentionStart = currentText?.match(/#\[[^\]]*$/);
    const waveMentionEnd = nextText?.match(/^[^\]]*\]/);
    if (waveMentionStart && waveMentionEnd) {
      try {
        if (reconstructSplitWaveMention(currentNode, nextNode, waveMentionStart, waveMentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split wave mention", error);
      }
    }
  }
  return false;
}
function InitialContentPlugin(t0) {
  const $ = _c(4);
  const {
    initialContent
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== initialContent) {
    t1 = () => {
      editor.update(() => {
        const normalizedContent = normalizeDropMarkdown(initialContent);
        $convertFromMarkdownString(normalizedContent, EDIT_MARKDOWN_TRANSFORMERS);
        const root = $getRoot();
        convertCodeNodesToFences(root);
        let needsAnotherPass = true;
        let passCount = 0;
        while (needsAnotherPass && passCount < MAX_MENTION_RECONSTRUCTION_PASSES) {
          const textNodes = root.getAllTextNodes();
          const hasUnprocessedMentions = textNodes.some(_temp);
          if (hasUnprocessedMentions) {
            break;
          }
          needsAnotherPass = processSplitMentions(textNodes);
          passCount = passCount + 1;
        }
        if (needsAnotherPass && passCount >= MAX_MENTION_RECONSTRUCTION_PASSES) {
          console.warn("Mention reconstruction reached max passes without converging");
        }
        root.selectEnd();
      });
    };
    t2 = [editor, initialContent];
    $[0] = editor;
    $[1] = initialContent;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp(node) {
  const text = node.getTextContent();
  return /@\[\w+\]/.test(text) || /#\[[^\]]+\]/.test(text);
}
function FocusPlugin(t0) {
  const $ = _c(4);
  const {
    isApp
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== isApp) {
    t1 = () => {
      const focusEditor = () => {
        editor.focus();
        requestAnimationFrame(_temp2);
      };
      if (isApp) {
        const timeouts = [setTimeout(focusEditor, 100), setTimeout(focusEditor, 350), setTimeout(focusEditor, 600)];
        return () => timeouts.forEach(clearTimeout);
      } else {
        focusEditor();
      }
    };
    t2 = [editor, isApp];
    $[0] = editor;
    $[1] = isApp;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {
  const contentEditable = document.querySelector("[contenteditable=\"true\"]") as HTMLElement;
  if (contentEditable && document.activeElement !== contentEditable) {
    contentEditable.focus();
    contentEditable.click();
  }
}
function KeyboardPlugin(t0) {
  const $ = _c(21);
  const {
    onSave,
    onCancel,
    isSaving,
    isMobileOrApp,
    initialContent,
    mentionsRef,
    waveMentionsRef
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  if ($[0] !== initialContent) {
    t1 = removeBlankLinePlaceholders(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sanitizedInitialContent = t1;
  let t2;
  if ($[2] !== editor || $[3] !== isMobileOrApp || $[4] !== isSaving || $[5] !== mentionsRef || $[6] !== onCancel || $[7] !== onSave || $[8] !== sanitizedInitialContent || $[9] !== waveMentionsRef) {
    t2 = () => {
      const removeEscapeListener = editor.registerCommand(KEY_ESCAPE_COMMAND, () => {
        onCancel();
        return true;
      }, COMMAND_PRIORITY_HIGH);
      const removeEnterListener = editor.registerCommand(KEY_ENTER_COMMAND, event => {
        if (mentionsRef.current?.isMentionsOpen() || waveMentionsRef.current?.isWaveMentionsOpen()) {
          return false;
        }
        if (isMobileOrApp) {
          return true;
        }
        if (event?.shiftKey) {
          return false;
        }
        if (!isSaving) {
          const currentMarkdown = exportDropMarkdown(editor.getEditorState(), EDIT_MARKDOWN_TRANSFORMERS);
          const sanitizedCurrentMarkdown = removeBlankLinePlaceholders(currentMarkdown);
          if (sanitizedCurrentMarkdown.trim() === sanitizedInitialContent.trim()) {
            onCancel();
          } else {
            onSave();
          }
        }
        return true;
      }, COMMAND_PRIORITY_HIGH);
      return () => {
        removeEscapeListener();
        removeEnterListener();
      };
    };
    $[2] = editor;
    $[3] = isMobileOrApp;
    $[4] = isSaving;
    $[5] = mentionsRef;
    $[6] = onCancel;
    $[7] = onSave;
    $[8] = sanitizedInitialContent;
    $[9] = waveMentionsRef;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== editor || $[12] !== initialContent || $[13] !== isMobileOrApp || $[14] !== isSaving || $[15] !== mentionsRef || $[16] !== onCancel || $[17] !== onSave || $[18] !== sanitizedInitialContent || $[19] !== waveMentionsRef) {
    t3 = [editor, onSave, onCancel, isSaving, isMobileOrApp, initialContent, mentionsRef, waveMentionsRef, sanitizedInitialContent];
    $[11] = editor;
    $[12] = initialContent;
    $[13] = isMobileOrApp;
    $[14] = isSaving;
    $[15] = mentionsRef;
    $[16] = onCancel;
    $[17] = onSave;
    $[18] = sanitizedInitialContent;
    $[19] = waveMentionsRef;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  useEffect(t2, t3);
  return null;
}
const EditDropLexical: React.FC<EditDropLexicalProps> = t0 => {
  const $ = _c(55);
  const {
    initialContent,
    initialMentions,
    initialWaveMentions,
    waveId,
    isSaving,
    onSave,
    onCancel
  } = t0;
  const [editorState, setEditorState] = useState(null);
  const [mentionedUsers, setMentionedUsers] = useState(initialMentions);
  const [mentionedWaves, setMentionedWaves] = useState(initialWaveMentions);
  const editorRef = useRef(null);
  const mentionsRef = useRef(null);
  const waveMentionsRef = useRef(null);
  const {
    isApp,
    isMobileDevice
  } = useDeviceInfo();
  const isMobileOrApp = isMobileDevice || isApp;
  let t1;
  if ($[0] !== initialContent) {
    t1 = normalizeDropMarkdown(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedInitialContent = t1;
  let t2;
  if ($[2] !== normalizedInitialContent) {
    t2 = addBlankLinePlaceholders(normalizedInitialContent);
    $[2] = normalizedInitialContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const editorInitialContent = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      namespace: "EditDropLexical",
      theme: ExampleTheme,
      onError: _temp3,
      nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, HorizontalRuleNode, AutoLinkNode, LinkNode, MentionNode, HashtagNode, WaveMentionNode, EmojiNode]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const initialConfig = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = editorState_0 => {
      setEditorState(editorState_0);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleEditorChange = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = user => {
      const newMention = {
        mentioned_profile_id: user.mentioned_profile_id,
        handle_in_content: user.handle_in_content
      };
      setMentionedUsers(prev => {
        if (prev.some(m => m.mentioned_profile_id === newMention.mentioned_profile_id)) {
          return prev;
        }
        return [...prev, newMention];
      });
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleMentionSelect = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = wave => {
      const newMention_0 = {
        wave_id: wave.wave_id,
        wave_name_in_content: wave.wave_name_in_content
      };
      setMentionedWaves(prev_0 => {
        if (prev_0.some(m_0 => m_0.wave_id === newMention_0.wave_id)) {
          return prev_0;
        }
        return [...prev_0, newMention_0];
      });
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleWaveMentionSelect = t6;
  let t7;
  if ($[8] !== editorState || $[9] !== mentionedUsers || $[10] !== mentionedWaves || $[11] !== normalizedInitialContent || $[12] !== onCancel || $[13] !== onSave) {
    t7 = () => {
      if (!editorState) {
        return;
      }
      const markdown = exportDropMarkdown(editorState, EDIT_MARKDOWN_TRANSFORMERS);
      const sanitizedMarkdown = removeBlankLinePlaceholders(markdown);
      if (sanitizedMarkdown.trim() === normalizedInitialContent.trim()) {
        onCancel();
        return;
      }
      onSave(sanitizedMarkdown, mentionedUsers, mentionedWaves);
    };
    $[8] = editorState;
    $[9] = mentionedUsers;
    $[10] = mentionedWaves;
    $[11] = normalizedInitialContent;
    $[12] = onCancel;
    $[13] = onSave;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleSave = t7;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <RichTextPlugin contentEditable={<div className="tw-relative"><ContentEditable className="tw-min-h-[40px] tw-w-full tw-resize-none tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-py-2.5 tw-pl-3 tw-pr-10 tw-text-sm tw-text-iron-100 tw-outline-none tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 focus:tw-border-primary-400 desktop-hover:hover:tw-scrollbar-thumb-iron-300" style={{
        fontFamily: "inherit",
        lineHeight: "1.4"
      }} /><CreateDropEmojiPicker top="tw-top-1" /></div>} placeholder={<div className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-2 tw-text-sm tw-text-iron-500">Edit message...</div>} ErrorBoundary={LexicalErrorBoundary} />;
    t9 = <OnChangePlugin onChange={handleEditorChange} />;
    t10 = <HistoryPlugin />;
    t11 = <PlainTextPastePlugin />;
    t12 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE} />;
    t13 = <ListPlugin />;
    t14 = <LinkPlugin />;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t15;
  if ($[22] !== waveId) {
    t15 = <NewMentionsPlugin ref={mentionsRef} waveId={waveId} onSelect={handleMentionSelect} />;
    $[22] = waveId;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <NewWaveMentionsPlugin ref={waveMentionsRef} onSelect={handleWaveMentionSelect} />;
    t17 = <EmojiPlugin />;
    $[24] = t16;
    $[25] = t17;
  } else {
    t16 = $[24];
    t17 = $[25];
  }
  let t18;
  if ($[26] !== editorInitialContent) {
    t18 = <InitialContentPlugin initialContent={editorInitialContent} />;
    $[26] = editorInitialContent;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== isApp) {
    t19 = <FocusPlugin isApp={isApp} />;
    $[28] = isApp;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== handleSave || $[31] !== isMobileOrApp || $[32] !== isSaving || $[33] !== normalizedInitialContent || $[34] !== onCancel) {
    t20 = <KeyboardPlugin onSave={handleSave} onCancel={onCancel} isSaving={isSaving} isMobileOrApp={isMobileOrApp} initialContent={normalizedInitialContent} mentionsRef={mentionsRef} waveMentionsRef={waveMentionsRef} />;
    $[30] = handleSave;
    $[31] = isMobileOrApp;
    $[32] = isSaving;
    $[33] = normalizedInitialContent;
    $[34] = onCancel;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t15 || $[37] !== t18 || $[38] !== t19 || $[39] !== t20) {
    t21 = <LexicalComposer initialConfig={initialConfig}><div ref={editorRef} className="tw-relative">{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}</div></LexicalComposer>;
    $[36] = t15;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== handleSave || $[42] !== isApp || $[43] !== isMobileDevice || $[44] !== onCancel) {
    t22 = !isApp && <div className="tw-mt-1 tw-flex tw-items-center tw-text-xs tw-text-iron-400">{!isMobileDevice && <>escape to </>}<button onClick={onCancel} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">cancel</button>{" "}{isMobileDevice ? "\u2022 " : "\u2022 enter to "}<button onClick={handleSave} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">save</button></div>;
    $[41] = handleSave;
    $[42] = isApp;
    $[43] = isMobileDevice;
    $[44] = onCancel;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== handleSave || $[47] !== isApp || $[48] !== isSaving || $[49] !== onCancel) {
    t23 = isApp && <div className="tw-mb-2 tw-mt-3"><div className="tw-flex tw-gap-x-2"><button onClick={onCancel} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-iron-300 tw-transition-colors tw-duration-150 active:tw-bg-iron-700 disabled:tw-opacity-50">Cancel</button><button onClick={handleSave} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-transition-colors tw-duration-150 active:tw-bg-primary-600 disabled:tw-opacity-50">{isSaving ? "Saving..." : "Save"}</button></div></div>;
    $[46] = handleSave;
    $[47] = isApp;
    $[48] = isSaving;
    $[49] = onCancel;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t21 || $[52] !== t22 || $[53] !== t23) {
    t24 = <div className="tw-w-full" data-editor-mode="true">{t21}{t22}{t23}</div>;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
};
export default EditDropLexical;
function _temp3(error) {
  console.error(error);
}